import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import { tokens } from '@pkg/components/FloorGameTicks/components/TicksMeter/tokens';
import { type GameStates } from '@pkg/hooks/fantasyWishlists';

const gradientAngle = '97.81deg';

export const Status = styled.span<{ $submissionState?: GameStates }>`
    font-weight: 700;
    font-size: 10px;
    line-height: 100%;
    text-align: center;
    letter-spacing: -0.02em;
    color: #ffffff;

    background: rgba(98, 250, 113, 0.1);
    backdrop-filter: blur(12px);
    border-radius: 40px;
    position: relative;

    height: 24px;
    padding: 0 10px;

    display: flex;
    align-items: center;
    justify-content: center;

    &:after {
        content: '';
        pointer-events: none;
        position: absolute;
        inset: 0;
        border-radius: inherit;
        padding: 1px;
        mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;

        ${({ $submissionState }) => {
            switch ($submissionState) {
                case 'closed': {
                    return css`
                        background: linear-gradient(
                            ${gradientAngle},
                            ${tokens.colors.statusMedian},
                            ${tokens.colors.statusNegative},
                            ${tokens.colors.statusNeutral} 90%
                        );
                    `;
                }
                case 'open': {
                    return css`
                        background: linear-gradient(
                            ${gradientAngle},
                            ${tokens.colors.statusPositive},
                            ${tokens.colors.statusBase} 100%
                        );
                    `;
                }
                case 'upcoming': {
                    return css`
                        background: linear-gradient(
                            ${gradientAngle},
                            ${tokens.colors.statusNeutral},
                            ${tokens.colors.statusBase},
                            ${tokens.colors.statusPositive} 130%
                        );
                    `;
                }
                default: {
                    return css`
                        background: linear-gradient(
                            ${gradientAngle},
                            ${tokens.colors.statusNeutral},
                            ${tokens.colors.statusBase} 40%,
                            transparent 90%
                        );
                    `;
                }
            }
        }}
    }
`;

export const GameWrapper = styled.div`
    width: 100%;
    padding: 2px;
    border-radius: 8px;
    display: flex;
    position: relative;
    overflow: hidden;
    background-color: rgba(255, 255, 255, 0.12);
`;

export const GameInside = styled.div`
    border-radius: 8px;
    background-color: #1d1b2f;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    user-select: none;
    position: relative;
    z-index: 1;
    width: 100%;
`;

export const ActiveBorder = styled(motion.div)`
    background: linear-gradient(91.77deg, #e3ae59 -34.76%, #90424c -10.86%, #442753 79.81%, #242135 115.58%);
    transition: all 0.2s ease-in-out;

    width: 100%;
    height: 100%;

    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
`;

export const TitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
`;

export const Label = styled.span`
    font-style: normal;
    font-weight: 600;
    font-size: 10px;
    line-height: 120%;
    margin-bottom: 3px;
    color: rgba(255, 255, 255, 0.5);
`;

export const Title = styled.div<{ $small?: boolean }>`
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    text-align: ${({ $small }) => $small && 'center'};
    line-height: 16px;

    letter-spacing: -0.4px;
    color: #ffffff;

    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 100%;
`;

export const ActionsWrapper = styled.div<{ $isActiveRoute?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    cursor: default;
    position: relative;
    z-index: 1;

    padding: 15px 15px 10px 15px;
    width: 100%;
    max-width: 100%;

    ${({ $isActiveRoute }) =>
        $isActiveRoute &&
        css`
            cursor: default;
        `}
`;

export const CollapseableArea = styled(motion.div)`
    width: 100%;
`;

export const CollapseableAreaPadding = styled.div`
    padding: 5px 15px 15px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const ButtonTextWrapper = styled.div`
    font-weight: bold;
    width: 100%;
    height: 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    font-size: 12px;
    background: linear-gradient(300.86deg, #523df1 39.32%, #976af6 92.76%);
    border-radius: 10px;

    transition: all 0.3s ease-in-out;
    &:hover {
        box-shadow: 0 0 24px rgba(94, 69, 242, 0.7);
    }
`;

export const TimeWrapper = styled.div`
    background: rgba(255, 255, 255, 0.1);
    padding: 6px 12px;
    border-radius: 8px;
`;

export const ButtonsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    position: relative;
    z-index: 1;
`;

export const HeaderBackground = styled.div<{ $image: string }>`
    position: absolute;
    top: 0;
    left: 0;
    height: 85px;
    isolation: isolate;
    width: 100%;
    background-image: url(${({ $image }) => $image});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center 0;
    background-color: rgba(255, 255, 255, 0.03);

    filter: blur(5px);

    &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background: #8080804e;
        background: linear-gradient(0deg, #1c1a2e, transparent),
            linear-gradient(321deg, rgba(255, 255, 255, 0) 0%, rgba(28, 26, 46, 1) 100%);
    }
`;

export const GameItemsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
`;

export const GameSkeleton = styled.div`
    width: 100%;
    height: 116px;

    border-radius: 8px;

    background: linear-gradient(0deg, rgba(255, 255, 255, 0.025) 0%, rgba(255, 255, 255, 0.01) 100%);
`;
